// src/hooks/useReactions.ts
// Hook pour gérer les réactions sur les posts et commentaires
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { queryKeys } from '../lib/queryClient';
import { ReactionType } from '../components/common/ReactionPicker';

interface ReactionCounts {
  like: number;
  love: number;
  pray: number;
  amen: number;
  sad: number;
  total: number;
}

interface ToggleReactionParams {
  targetId: number;
  targetType: 'post' | 'comment';
  reactionType: ReactionType | null;
  currentReaction?: ReactionType | null;
}

interface ToggleReactionResponse {
  success: boolean;
  reaction_type: ReactionType | null;
  reactions_count: ReactionCounts;
}

// Toggle reaction on a post or comment
export const useToggleReaction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ targetId, targetType, reactionType, currentReaction }: ToggleReactionParams): Promise<ToggleReactionResponse> => {
      const endpoint = targetType === 'post'
        ? `/liturgical/reactions/${targetId}/toggle`
        : `/post-comments/${targetId}/reactions/toggle`;

      // If removing reaction or changing reaction type
      if (reactionType === null) {
        // Remove current reaction
        await api.delete(endpoint);
        return {
          success: true,
          reaction_type: null,
          reactions_count: { like: 0, love: 0, pray: 0, amen: 0, sad: 0, total: 0 },
        };
      }

      // Add or change reaction
      const response = await api.post(endpoint, { reaction_type: reactionType });
      return response.data;
    },
    onMutate: async ({ targetId, targetType, reactionType, currentReaction }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.posts.infinite() });

      // Get current data
      const previousData = queryClient.getQueryData(queryKeys.posts.infinite());

      // Optimistic update for posts
      if (targetType === 'post') {
        queryClient.setQueryData(queryKeys.posts.infinite(), (old: any) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page: any) => ({
              ...page,
              posts: page.posts.map((post: any) => {
                if (post.id !== targetId) return post;

                // Calculate new reaction counts
                const reactions = { ...post.reactions_by_type } || {};

                // Decrement old reaction
                if (currentReaction && reactions[currentReaction]) {
                  reactions[currentReaction] = Math.max(0, reactions[currentReaction] - 1);
                }

                // Increment new reaction
                if (reactionType) {
                  reactions[reactionType] = (reactions[reactionType] || 0) + 1;
                }

                // Calculate total
                const total = Object.values(reactions).reduce((sum: number, count: any) => sum + (count || 0), 0);

                return {
                  ...post,
                  user_reaction: reactionType,
                  reactions_by_type: reactions,
                  reactions_count: total,
                };
              }),
            })),
          };
        });
      }

      return { previousData };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(queryKeys.posts.infinite(), context.previousData);
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      // queryClient.invalidateQueries({ queryKey: queryKeys.posts.infinite() });
    },
  });
};

// Get reaction details for a post (who reacted with what)
export const useReactionDetails = (postId: number) => {
  // This could be a query hook if needed
  return {
    fetchDetails: async () => {
      const response = await api.get(`/liturgical-posts/${postId}/reactions`);
      return response.data;
    },
  };
};

export default useToggleReaction;
